import classNames from "classnames/bind";
import { isEqual } from "es-toolkit/compat";
import React, {
  cloneElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { usePrevious } from "../utils/hooks/usePrevious";
import styles from "./Accordion.module.scss";
import AccordionPanel from "./AccordionPanel";
import type { AccordionProps } from "./types";

const cx = classNames.bind(styles);

/**
 * The accordion component delivers large amounts of content in a small space through progressive disclosure.
 */
const Accordion = ({
  defaultActiveKeys,
  onlyAllowOneOpen = false,
  shouldApplyDefaultsWhenChanged = false,
  shouldKeepFirstOpen = false,
  className,
  onChange,
  children,
}: AccordionProps) => {
  const defaultsApplied = useRef(false);
  const previousDefaultActiveKeys = usePrevious(defaultActiveKeys);
  const [activeKeys, setActiveKeys] = useState<Array<string | number>>(
    defaultActiveKeys ?? []
  );

  // Only render AccordionPanel children
  const displayPanels = useMemo(() => {
    if (Array.isArray(children)) {
      return children.flat().filter((child) => {
        return (
          child?.type === AccordionPanel ||
          child?.type?.displayName === "AccordionPanel"
        );
      });
    } else {
      if (
        // @ts-expect-error: this property does exist because of React
        children?.type === AccordionPanel ||
        // @ts-expect-error: this property does exist because of React
        children?.type?.displayName === "AccordionPanel"
      ) {
        return [children];
      } else {
        return [];
      }
    }
  }, [children]);

  const onOpenPanel = (dataKey: string | number) => {
    const newKeys = onlyAllowOneOpen ? [dataKey] : [...activeKeys, dataKey];

    setActiveKeys(newKeys);
    onChange?.(newKeys);
  };

  const onClosePanel = (dataKey: string | number) => {
    const newKeys = activeKeys.filter((current) => current !== dataKey);

    if (
      newKeys.length === 0 &&
      shouldKeepFirstOpen &&
      displayPanels[0]?.props?.dataKey
    ) {
      setActiveKeys([displayPanels[0]?.props?.dataKey]);
      onChange?.([displayPanels[0]?.props?.dataKey]);
    } else {
      setActiveKeys(newKeys);
      onChange?.(newKeys);
    }
  };

  // on first render, defaults are applied
  useEffect(() => {
    defaultsApplied.current = true;
  }, []);

  // whenever default props change, defaults are not applied
  useEffect(() => {
    if (
      !shouldApplyDefaultsWhenChanged ||
      !defaultsApplied.current ||
      isEqual(previousDefaultActiveKeys, defaultActiveKeys)
    )
      return;

    defaultsApplied.current = false;
  }, [defaultActiveKeys, shouldApplyDefaultsWhenChanged]);

  useEffect(() => {
    if (!shouldApplyDefaultsWhenChanged) return;

    let timeoutId: NodeJS.Timeout;

    // if defaults are available and not applied yet, apply them
    if (!defaultsApplied.current && defaultActiveKeys?.length) {
      timeoutId = setTimeout(() => {
        setActiveKeys(defaultActiveKeys);
        onChange?.(defaultActiveKeys);
        defaultsApplied.current = true;
      }, 300);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [shouldApplyDefaultsWhenChanged, defaultActiveKeys]);

  return (
    <div className={cx("container", className)}>
      {displayPanels.map((panel) =>
        cloneElement(panel, {
          key: panel.props.dataKey,
          isOpen: activeKeys.includes(panel.props.dataKey),
          onOpen: (dataKey: string | number) => {
            onOpenPanel(dataKey);
            if (panel.props.onOpen) {
              panel.props.onOpen();
            }
          },
          onClose: (dataKey: string | number) => {
            onClosePanel(dataKey);

            if (panel.props.onClose) {
              panel.props.onClose();
            }
          },
        })
      )}
    </div>
  );
};

Accordion.AccordionPanel = AccordionPanel;

export default Accordion;
